import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Icon,
  Input,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import { MdContentCopy } from 'react-icons/md';
import Card from 'components/card/Card';
import { TimeCombobox } from 'components/ui/TimeCombobox';
import Dropdown from 'components/ui/Dropdown';
import { useProfessionalAvailability, useBatchReplaceAvailability } from 'hooks/useAvailability';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { Availability } from 'services/availabilityService';

// ─── Day helpers ──────────────────────────────────────────────────────────────

const DAYS = [
  { id: 'monday',    abbr: 'Lun' },
  { id: 'tuesday',   abbr: 'Mar' },
  { id: 'wednesday', abbr: 'Mié' },
  { id: 'thursday',  abbr: 'Jue' },
  { id: 'friday',    abbr: 'Vie' },
  { id: 'saturday',  abbr: 'Sáb' },
  { id: 'sunday',    abbr: 'Dom' },
] as const;

type DayId = typeof DAYS[number]['id'];

interface TimePeriod { startTime: string; endTime: string; }
interface DayConfig  { enabled: boolean; periods: TimePeriod[]; }

const DEFAULT_DAYS: Record<DayId, DayConfig> = {
  monday:    { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  tuesday:   { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  wednesday: { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  thursday:  { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  friday:    { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  saturday:  { enabled: false, periods: [{ startTime: '08:00', endTime: '13:00' }] },
  sunday:    { enabled: false, periods: [{ startTime: '08:00', endTime: '13:00' }] },
};


const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2).toString().padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

// DB stores time as HH:MM:SS — strip seconds before using in the form
const toHHMM = (t: string) => t.slice(0, 5);

function localDateStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nextMondayStr(): string {
  const d = new Date();
  const day = d.getDay();
  const daysUntil = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + daysUntil);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function tomorrowStr(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function slotsToConfig(slots: Availability[]): { days: Record<DayId, DayConfig>; duration: number } {
  const today = localDateStr();

  // Only use slots that are currently active (not future, not expired)
  const current = slots.filter(s => {
    if (s.effectiveFrom && s.effectiveFrom > today) return false;
    if (s.effectiveUntil && s.effectiveUntil < today) return false;
    return true;
  });

  const days = {} as Record<DayId, DayConfig>;
  DAYS.forEach(d => {
    days[d.id] = { enabled: false, periods: [{ startTime: '08:00', endTime: '17:00' }] };
  });

  const grouped: Record<string, TimePeriod[]> = {};
  current.forEach(slot => {
    if (!grouped[slot.dayOfWeek]) grouped[slot.dayOfWeek] = [];
    grouped[slot.dayOfWeek].push({
      startTime: toHHMM(slot.startTime),
      endTime: toHHMM(slot.endTime),
    });
  });

  Object.entries(grouped).forEach(([day, periods]) => {
    if (day in days) {
      days[day as DayId] = { enabled: true, periods };
    }
  });

  const duration = current.length > 0 ? current[0].slotDurationMinutes : 30;
  return { days, duration };
}

// ─── Remove-period icon (octagon minus) ──────────────────────────────────────

function OctMinusIcon() {
  return (
    <Box as="svg" xmlns="http://www.w3.org/2000/svg" width="16px" height="16px"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z" />
      <path d="M8 12h8" />
    </Box>
  );
}

function AddCircleIcon() {
  return (
    <Box as="svg" xmlns="http://www.w3.org/2000/svg" width="18px" height="18px"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" /><path d="M12 8v8" />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AvailabilitySettingsCard(props: { [x: string]: any }) {
  const { ...rest } = props;
  const toast = useToast();
  const { data: me } = useCurrentDbUser();
  const { data: slots, isLoading: loadingSlots } = useProfessionalAvailability(me?.id);
  const batchMut   = useBatchReplaceAvailability(me?.id ?? '');

  const [days,          setDays]          = useState<Record<DayId, DayConfig>>(DEFAULT_DAYS);
  const [duration,      setDuration]      = useState(30);
  const [error,         setError]         = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');

  // Pre-fill from existing availability
  useEffect(() => {
    if (slots && slots.length > 0) {
      const { days: d, duration: dur } = slotsToConfig(slots);
      setDays(d);
      setDuration(dur);
      setEffectiveFrom(nextMondayStr());
    }
  }, [slots]);

  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor       = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const borderColor      = useColorModeValue('gray.100', 'whiteAlpha.100');
  const vigenciaBg       = useColorModeValue('blue.50', 'navy.700');
  const vigenciaBorder   = useColorModeValue('blue.200', 'blue.700');

  const toggleDay = (dayId: DayId) =>
    setDays(prev => ({ ...prev, [dayId]: { ...prev[dayId], enabled: !prev[dayId].enabled } }));

  const addPeriod = (dayId: DayId) =>
    setDays(prev => ({
      ...prev,
      [dayId]: {
        enabled: true,
        periods: [...prev[dayId].periods, { startTime: '09:00', endTime: '17:00' }],
      },
    }));

  const removePeriod = (dayId: DayId, idx: number) => {
    const periods = days[dayId].periods;
    if (periods.length === 1) {
      toggleDay(dayId);
    } else {
      setDays(prev => ({
        ...prev,
        [dayId]: { ...prev[dayId], periods: prev[dayId].periods.filter((_, i) => i !== idx) },
      }));
    }
  };

  const updateTime = (dayId: DayId, idx: number, field: 'startTime' | 'endTime', value: string) =>
    setDays(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        periods: prev[dayId].periods.map((p, i) => i === idx ? { ...p, [field]: value } : p),
      },
    }));

  const copyToAll = (dayId: DayId) => {
    const src = days[dayId];
    setDays(prev => {
      const next = { ...prev };
      DAYS.forEach(d => {
        if (next[d.id].enabled && d.id !== dayId) {
          next[d.id] = { ...next[d.id], periods: [...src.periods] };
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    if (!me?.id) return;

    const activeDays = DAYS.filter(d => days[d.id].enabled);
    if (activeDays.length === 0) {
      setError('Selecciona al menos un día de atención.');
      return;
    }

    for (const day of activeDays) {
      for (const p of days[day.id].periods) {
        if (p.startTime >= p.endTime) {
          setError(`${day.abbr}: la hora de inicio debe ser anterior a la hora fin.`);
          return;
        }
      }
    }

    const slots = activeDays.flatMap(day =>
      days[day.id].periods.map(p => ({
        dayOfWeek: day.id,
        startTime: p.startTime,
        endTime: p.endTime,
        slotDurationMinutes: duration,
        zonaHoraria: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Guayaquil',
      }))
    );

    try {
      await batchMut.mutateAsync({ slots, effectiveFrom: effectiveFrom || undefined });
      toast({
        title: 'Disponibilidad guardada',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Error al guardar.'));
    }
  };

  const isSaving = batchMut.isPending;

  return (
    <Card {...rest}>
      {/* Header */}
      <Flex align='center' justify='space-between' mb='24px'>
        <Box>
          <Text color={textColorPrimary} fontWeight='bold' fontSize='xl' mb='2px'>
            Ajustes de Agenda
          </Text>
          <Text color={mutedColor} fontSize='sm'>
            Configura tu jornada semanal de atención.
          </Text>
        </Box>
        {loadingSlots && <Spinner size='sm' color='brand.500' />}
      </Flex>

      {/* Configuración de sesión */}
      <Box mb='24px'>
        <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase'
          letterSpacing='0.1em' mb='10px'>
          Configuración de Sesión
        </Text>
        <Box maxW='200px'>
          <Text fontSize='xs' fontWeight='600' color={mutedColor} mb='6px'>
            Duración de sesión
          </Text>
          <Dropdown
            label={
              duration === 15 ? '15 min' :
              duration === 30 ? '30 min' :
              duration === 45 ? '45 min' :
              duration === 60 ? '1 h'    :
              duration === 90 ? '1.5 h'  :
              duration === 120 ? '2 h'   :
              `${duration} min`
            }
            items={[
              { label: '15 min', value: 15 },
              { label: '30 min', value: 30 },
              { label: '45 min', value: 45 },
              { label: '1 h',    value: 60 },
              { label: '1.5 h',  value: 90 },
              { label: '2 h',    value: 120 },
            ]}
            onSelect={v => setDuration(Number(v))}
            w='100%'
            variant='outline'
            size='sm'
          />
        </Box>
      </Box>

      {/* Weekly schedule */}
      <Box mb='20px'>
        <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase'
          letterSpacing='0.1em' mb='4px'>
          Horario Semanal
        </Text>

        {DAYS.map(day => {
          const cfg = days[day.id];
          return (
            <Flex
              key={day.id}
              align='flex-start'
              py='14px'
              borderBottom='1px solid'
              borderColor={borderColor}
              gap='16px'
              _last={{ borderBottom: 'none' }}
            >
              {/* Day toggle + label */}
              <Box
                as='button'
                type='button'
                onClick={() => toggleDay(day.id)}
                flexShrink={0}
                w='48px'
                pt='4px'
              >
                <Text
                  fontSize='sm'
                  fontWeight='700'
                  textTransform='uppercase'
                  letterSpacing='0.05em'
                  color={cfg.enabled ? textColorPrimary : mutedColor}
                  _hover={{ color: cfg.enabled ? 'red.400' : 'brand.500' }}
                  transition='color 0.15s'
                >
                  {day.abbr}
                </Text>
              </Box>

              {/* Periods */}
              <Flex flex={1} direction='column' gap='6px'>
                {!cfg.enabled ? (
                  <Text fontSize='sm' color={mutedColor} pt='4px'>No disponible</Text>
                ) : (
                  cfg.periods.map((p, idx) => (
                    <Flex key={idx} align='center' gap='8px'>
                      <TimeCombobox
                        value={p.startTime}
                        onChange={v => updateTime(day.id, idx, 'startTime', v)}
                        options={TIME_OPTIONS}
                        listMaxH='170px'
                        flex={1}
                        w='0'
                      />
                      <Text fontSize='sm' color={mutedColor} flexShrink={0} userSelect='none'>–</Text>
                      <TimeCombobox
                        value={p.endTime}
                        onChange={v => updateTime(day.id, idx, 'endTime', v)}
                        options={TIME_OPTIONS}
                        listMaxH='170px'
                        flex={1}
                        w='0'
                      />
                      <Box
                        as='button'
                        type='button'
                        onClick={() => removePeriod(day.id, idx)}
                        color={mutedColor}
                        _hover={{ color: 'red.400' }}
                        transition='color 0.15s'
                        cursor='pointer'
                        bg='transparent'
                        border='none'
                        p='0'
                        display='flex'
                        alignItems='center'
                        flexShrink={0}
                        title='Quitar periodo'
                      >
                        <OctMinusIcon />
                      </Box>
                    </Flex>
                  ))
                )}
              </Flex>

              {/* Actions */}
              <Flex gap='6px' align='center' flexShrink={0} pt='2px'>
                {cfg.enabled && (
                  <Box
                    as='button'
                    type='button'
                    onClick={() => copyToAll(day.id)}
                    color={mutedColor}
                    _hover={{ color: textColorPrimary }}
                    transition='color 0.15s'
                    cursor='pointer'
                    bg='transparent'
                    border='none'
                    p='0'
                    display='flex'
                    alignItems='center'
                    title='Copiar horario a todos los días activos'
                  >
                    <Icon as={MdContentCopy} w='15px' h='15px' />
                  </Box>
                )}
                <Box
                  as='button'
                  type='button'
                  onClick={() => cfg.enabled ? addPeriod(day.id) : toggleDay(day.id)}
                  color='brand.500'
                  _hover={{ color: 'brand.600' }}
                  transition='color 0.15s'
                  cursor='pointer'
                  bg='transparent'
                  border='none'
                  p='0'
                  display='flex'
                  alignItems='center'
                  title={cfg.enabled ? 'Añadir franja' : 'Activar día'}
                >
                  <AddCircleIcon />
                </Box>
              </Flex>
            </Flex>
          );
        })}
      </Box>

      {/* Vigencia */}
      {slots && slots.length > 0 && (
        <Box
          mb='20px'
          p='14px'
          borderRadius='12px'
          bg={vigenciaBg}
          border='1px solid'
          borderColor={vigenciaBorder}
        >
          <Text fontSize='10px' fontWeight='700' color='blue.600' textTransform='uppercase'
            letterSpacing='0.1em' mb='6px'>
            Fecha de vigencia
          </Text>
          <Text fontSize='xs' color={mutedColor} mb='10px'>
            Las citas ya agendadas se respetan hasta esta fecha. Deja el campo vacío para aplicar el cambio de inmediato.
          </Text>
          <Input
            type='date'
            value={effectiveFrom}
            min={tomorrowStr()}
            onChange={e => setEffectiveFrom(e.target.value)}
            size='sm'
            borderRadius='10px'
            maxW='200px'
            bg='white'
            _dark={{ bg: 'navy.800' }}
            borderColor={vigenciaBorder}
          />
          {effectiveFrom && (
            <Text fontSize='xs' color='blue.600' mt='6px' fontWeight='600'>
              Nuevo horario activo desde {new Date(effectiveFrom + 'T12:00:00').toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' })}.
              El horario actual sigue vigente hasta el día anterior.
            </Text>
          )}
        </Box>
      )}

      {error && (
        <Text fontSize='sm' color='red.500' mb='12px'>{error}</Text>
      )}

      <Flex justify='flex-end'>
        <Button onClick={handleSave} isLoading={isSaving}>
          Guardar
        </Button>
      </Flex>
    </Card>
  );
}
